import { useEffect, useRef, useState } from 'react'
import { VoteBar } from './VoteBar.jsx'
import { ShareButton } from './ShareButton.jsx'
import SentimentTrend from './SentimentTrend.jsx'
import { SocialProofChip } from './SocialProofChip.jsx'
import {
  computeSnapshot,
  loadSnapshots,
  saveSnapshot,
  shouldSnapshot,
} from '../../utils/snapshots.js'
import { residentPercentages } from '../../utils/shareIntent.js'

const CHOICE_EMOJI = { Yes: '👍', No: '👎', Unsure: '🤔' }
const REVEAL_MS = 2500

// Break a summary into displayable paragraphs.
//   - If the source already has paragraph breaks (\n\n), use those — the
//     bot strips Congress.gov's <p> tags into double newlines, so this
//     preserves the CRS author's structure.
//   - Otherwise fall back to grouping sentences in chunks of three so
//     dense single-paragraph summaries stop reading as a wall of text.
function splitIntoParagraphs(text) {
  if (!text) return []
  const explicit = text
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean)
  if (explicit.length > 1) return explicit

  const sentences = text.match(/[^.!?]+[.!?]+(?:\s|$)/g) || [text]
  if (sentences.length <= 3) return [text.trim()]
  const out = []
  for (let i = 0; i < sentences.length; i += 3) {
    out.push(sentences.slice(i, i + 3).join('').trim())
  }
  return out
}

function ExpandableSummary({ text, maxChars = 280 }) {
  const [expanded, setExpanded] = useState(false)
  if (!text) return null
  const trimmed = text.trim()
  const isLong = trimmed.length > maxChars

  if (!isLong) {
    return <p className="card-summary">{trimmed}</p>
  }

  if (expanded) {
    const paras = splitIntoParagraphs(trimmed)
    return (
      <div className="card-body">
        {paras.map((p, i) => (
          <p key={i} className="card-summary">{p}</p>
        ))}
        <button
          type="button"
          className="card-toggle"
          onClick={() => setExpanded(false)}
        >
          Show less ↑
        </button>
      </div>
    )
  }

  // Truncate at the last word boundary before maxChars.
  const cut = trimmed.slice(0, maxChars).replace(/\s+\S*$/, '')
  return (
    <div className="card-body">
      <p className="card-summary">{cut}…</p>
      <button
        type="button"
        className="card-toggle"
        onClick={() => setExpanded(true)}
      >
        Show more ↓
      </button>
    </div>
  )
}

function fmtDate(d) {
  if (!d) return ''
  return d.toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
  })
}

// UX42: condensed stage labels for the TL;DR strip. Values come from
// the bot's bill_stage tag (classified in bots/vote.py and
// bots/state_bot.py) and don't require any LLM to produce.
const STAGE_LABEL = {
  introduced: 'Introduced',
  committee:  'In committee',
  floor:      'Floor vote',
  passed_one: 'Passed one chamber',
  passed_both:'Passed both chambers',
  signed:     'Signed into law',
  dead:       'Dead',
  vetoed:     'Vetoed',
}

// Pull the most recent action from the server-provided bill_actions
// array (UX15). Returns null if the array is empty or missing — the
// strip then shows a "—" placeholder instead of an empty cell.
function lastBillAction(billActions) {
  if (!Array.isArray(billActions) || billActions.length === 0) return null
  const last = billActions[billActions.length - 1]
  const date = last?.action_date
  const text = (last?.action_text || '').trim()
  if (!date || !text) return null
  let parsedDate = null
  try { parsedDate = new Date(date) } catch { /* noop */ }
  const short = text.length > 48 ? text.slice(0, 47).trim() + '…' : text
  return {
    date: parsedDate && !isNaN(parsedDate) ? fmtDate(parsedDate) : date,
    text: short,
  }
}

// Describe where voting stands: open / closing soon / closed.
function votingStatus(endsAt) {
  if (!endsAt) return 'Open'
  const now = Date.now()
  const closes = endsAt.getTime()
  if (closes < now) return 'Voting closed'
  const daysLeft = Math.ceil((closes - now) / (1000 * 60 * 60 * 24))
  if (daysLeft <= 1) return 'Closes today'
  if (daysLeft <= 14) return `Closes in ${daysLeft} days`
  return `Closes ${fmtDate(endsAt)}`
}

function BillTldr({ stage, action, status }) {
  return (
    <dl className="bill-tldr">
      <div className="bill-tldr-cell">
        <dt>Stage</dt>
        <dd>{stage}</dd>
      </div>
      <div className="bill-tldr-cell">
        <dt>Last action</dt>
        <dd>
          {action ? (
            <>
              <span className="bill-tldr-date">{action.date}</span>
              <span className="bill-tldr-sep"> · </span>
              <span className="bill-tldr-text">{action.text}</span>
            </>
          ) : (
            <span className="bill-tldr-muted">—</span>
          )}
        </dd>
      </div>
      <div className="bill-tldr-cell">
        <dt>Voting</dt>
        <dd>{status}</dd>
      </div>
    </dl>
  )
}

function FullSummary({ text }) {
  const [open, setOpen] = useState(false)
  if (!text) return null
  if (!open) {
    return (
      <button
        type="button"
        className="card-toggle"
        onClick={() => setOpen(true)}
      >
        Full summary ↓
      </button>
    )
  }
  const paras = splitIntoParagraphs(text.trim())
  return (
    <div className="card-body">
      {paras.map((p, i) => (
        <p key={i} className="card-summary">{p}</p>
      ))}
      <button
        type="button"
        className="card-toggle"
        onClick={() => setOpen(false)}
      >
        Hide summary ↑
      </button>
    </div>
  )
}

function variantForOption(label) {
  const k = label.toLowerCase()
  if (k === 'yes' || k === 'aye') return 'yes'
  if (k === 'no'  || k === 'nay') return 'no'
  if (k.startsWith('uns') || k.startsWith('abst')) return 'unsure'
  return 'yes'
}

export function BillCard({ item, tally, userVote, onVote, onViewMetrics, onShareQuote, isLoggedIn, featured, activity }) {
  // UX40: inline dopamine reveal. When userVote transitions (undefined
  // → set OR one choice → another), briefly overlay the card body with
  // the choice + "X% of your district" stat. Auto-dismisses in 2.5s.
  // Scoped to the card itself; the full share dialog stays on the
  // Share button in the card foot.
  const prevVoteRef = useRef(userVote)
  const [reveal, setReveal] = useState(null)
  useEffect(() => {
    if (userVote && userVote !== prevVoteRef.current) {
      // Snapshot the stat at the moment of the vote so a tally update
      // mid-reveal doesn't flicker the number.
      const isUserResident = item.resident === true
      const addToRes = isUserResident ? 1 : 0
      const t = tally || {}
      const yesRes    = (t.Yes?.resident    || 0) + (userVote === 'Yes'    ? addToRes : 0)
      const noRes     = (t.No?.resident     || 0) + (userVote === 'No'     ? addToRes : 0)
      const unsureRes = (t.Unsure?.resident || 0) + (userVote === 'Unsure' ? addToRes : 0)
      const totalRes = yesRes + noRes + unsureRes
      const choiceRes =
        userVote === 'Yes' ? yesRes : userVote === 'No' ? noRes : unsureRes
      const pct = totalRes ? Math.round((choiceRes / totalRes) * 100) : 0
      const isFirst = totalRes === addToRes
      setReveal({ choice: userVote, pct, isUserResident, isFirst })
      const timer = setTimeout(() => setReveal(null), REVEAL_MS)
      prevVoteRef.current = userVote
      return () => clearTimeout(timer)
    }
    prevVoteRef.current = userVote
  }, [userVote, tally, item.resident])
  const totalAllOptions = item.options.reduce((sum, opt) => {
    const t = tally?.[opt]
    return sum + (t?.resident || 0) + (t?.nonResident || 0)
  }, 0)

  // Persist a daily snapshot of the running tally for sentiment-trend
  // sparklines. shouldSnapshot() rate-limits this to once per day per poll.
  const [snapshots, setSnapshots] = useState(() => loadSnapshots(item.id))
  useEffect(() => {
    if (totalAllOptions === 0) return
    if (!shouldSnapshot(item.id)) return
    const snap = computeSnapshot(tally, item.billStage)
    setSnapshots(saveSnapshot(item.id, snap))
  }, [tally, item.id, item.billStage, totalAllOptions])

  const residentTotal = item.options.reduce(
    (s, o) => s + (tally?.[o]?.resident || 0), 0)
  const nonResidentTotal = item.options.reduce(
    (s, o) => s + (tally?.[o]?.nonResident || 0), 0)

  const levelClass =
    item.level === 'federal' ? 'federal' :
    item.level === 'state' ? 'state' :
    item.level === 'city' ? 'city' :
    item.level === 'user' ? 'user' : 'federal'

  const cardClass = [
    'card',
    item.level === 'user' && 'card-user',
    featured && 'card-featured',
  ].filter(Boolean).join(' ')

  return (
    <article className={cardClass} style={{ position: 'relative' }} data-poll-id={item.id}>
      {reveal && (
        <div className="bill-card-reveal" aria-live="polite">
          <div className="bill-card-reveal-choice">
            <span className="bill-card-reveal-emoji" aria-hidden="true">
              {CHOICE_EMOJI[reveal.choice] || '✓'}
            </span>
            <span>
              You voted <strong>{reveal.choice}</strong>
            </span>
          </div>
          {!reveal.isFirst && (
            <div className="bill-card-reveal-stat">
              <span className="bill-card-reveal-pct">{reveal.pct}%</span>
              <span className="bill-card-reveal-stat-text">
                of {reveal.isUserResident ? 'your district' : 'residents'} picked the same choice
              </span>
            </div>
          )}
          {reveal.isFirst && (
            <div className="bill-card-reveal-stat-text">
              You&rsquo;re the first voter {reveal.isUserResident ? 'in your district' : 'on this poll'}.
            </div>
          )}
        </div>
      )}
      <div className="card-head">
        <div className="card-meta">
          <span className={`badge ${levelClass}`}>{item.levelLabel}</span>
          {item.billId && <span className="bill-id">{item.billId}</span>}
          {item.categories?.[0] && (
            <span className="badge topic" title={item.categories.join(', ')}>
              Topic: {item.categories[0]}
            </span>
          )}
          {item.resident === false && (
            <span className="badge nonresident">Non-resident view</span>
          )}
          {/* UX45: social-proof chip. Rendered last so the "alive now"
              signal reads next to level + topic without pushing them. */}
          <SocialProofChip activity={activity} />
        </div>
      </div>

      {(() => {
        // Bill name is the headline; summary is supporting body text.
        // If only one of the two is present, fall back gracefully.
        const headline = item.title || item.plainSummary || 'Untitled poll'
        const body = item.plainSummary && item.plainSummary !== item.title
          ? item.plainSummary
          : null
        // UX57: pulse polls (bot=pulse) don't have legislative stage
        // or last-action data — the TL;DR strip is meaningless for a
        // sentiment question. Suppress it and let the summary breathe.
        const isPulse = item.botName === 'pulse'
        if (isPulse) {
          return (
            <>
              <h3 className="card-title">{headline}</h3>
              {body && <p className="card-summary">{body}</p>}
            </>
          )
        }
        // UX42: scannable TL;DR row above the full summary. All three
        // cells come from existing bot event tags + server-side
        // bill_actions enrichment; no LLM.
        const stageLabel = STAGE_LABEL[item.billStage] || 'In progress'
        const action = lastBillAction(item.billActions)
        const status = votingStatus(item.endsAt)
        return (
          <>
            <h3 className="card-title">{headline}</h3>
            <BillTldr stage={stageLabel} action={action} status={status} />
            {body && <FullSummary text={body} />}
          </>
        )
      })()}

      {item.sourceUrl && (
        <a
          className="card-source"
          href={item.sourceUrl}
          target="_blank"
          rel="noreferrer noopener"
        >
          View source ↗
        </a>
      )}

      <div className="vote-block">
        {item.options.map((opt) => {
          const t = tally?.[opt] || { resident: 0, nonResident: 0 }
          return (
            <VoteBar
              key={opt}
              label={opt}
              optionKey={opt}
              variant={variantForOption(opt)}
              residentCount={t.resident}
              nonResidentCount={t.nonResident}
              totalAllOptions={totalAllOptions}
              voted={userVote === opt}
              disabled={!isLoggedIn}
              onVote={(choice) => onVote?.(item, choice)}
            />
          )
        })}
        {totalAllOptions === 0 && (
          <div className="zero-vote-hint">Be the first to vote.</div>
        )}
      </div>

      <div className="card-foot">
        <span>
          {totalAllOptions} votes · {residentTotal} resident · {nonResidentTotal} non-resident
        </span>
        <span style={{ display: 'inline-flex', gap: 12, alignItems: 'center' }}>
          {item.endsAt ? `Closes ${fmtDate(item.endsAt)}` : `Posted ${fmtDate(item.postedAt)}`}
          <ShareButton item={item} tally={tally} userVote={userVote} isLoggedIn={isLoggedIn} onPostQuote={onShareQuote} />
        </span>
      </div>

      <SentimentTrend
        snapshots={snapshots}
        billActions={item.billActions || []}
      />

      {onViewMetrics && (
        <div className="card-actions">
          <button
            type="button"
            className="card-toggle"
            onClick={() => onViewMetrics(item.id)}
          >
            Detailed metrics →
          </button>
        </div>
      )}
    </article>
  )
}
